// src/encounter/structured-note/note-adapter.ts
//
// PROD-2743 · frame v2 ONLY (`?ef=frameV2`). The data side of the structured-note mount: which
// template item feeds which approved R6c block (signal-ds #434), and what each block's change
// writes back. CONTRACT-template-to-encounter-v1 §1b is the table this file implements.
//
// PURE. No React, no fetch. Every function reads the ONE whole-encounter QuestionnaireResponse
// and answers either "what does the block show" or "which answers change". The blocks keep no
// copy of an answer: they are handed a projection of the response on every render, and every
// change goes back through `applyUpdates` onto the same response (§1b: "One live response").
//
// THE WIRE IS STILL BOOLEAN for findings (G18 / PROD-2821 has not shipped the five-state model):
//   true  → Reports (history) / Abnormal (an exam +/- finding) / Normal (an exam normal statement)
//   false → Denies (history)  / Normal (an exam +/- finding)
//   unset → NOT ASKED / NOT EXAMINED — silence. Never "No".
// A state the wire cannot hold (Doesn't know, Declined, Not examined, Unable to assess) is never
// OFFERED: the blocks are handed `ROS_STORABLE` / `EXAM_STORABLE` as `allowedStates` (signal-ds
// #434), so their "⋯" shows none of them until G18 stores them. Should a block emit one anyway,
// nothing is written: "unset" would record an asked question as not asked, false a "No" nobody said.
//
// BULK SAFETY (AC-07.2 / AC-08.1) is enforced HERE as well as in the blocks (defence in depth): a
// change the block reports as `'bulk'`, or one that touches more than one item, may only FILL an
// unanswered item. It never overwrites a recorded answer, never answers a risk item (`isBulkExempt`,
// inherited down the tree: flagged, or its wording names suicide / self-harm / harm to others), and
// on the exam it only ever sets a normal statement, never a +/- finding, and none while the system
// holds an abnormal finding — the same eligibility rule the blocks' previews count.
import {
  qrIsNormalChip,
  qrIsPosNegChip,
  qrItemEnabled,
  qrOptionValue,
  type ChangeOrigin,
  type ExamFinding,
  type ExamFindingState,
  type ExamFindings,
  type ExamSystem,
  type PickListOption,
  type QAnswerOption,
  type QAnswerValue,
  type QItem,
  type Questionnaire,
  type QuestionnaireResponse,
  type RosFinding,
  type RosFindings,
  type RosSystem,
  type SymptomState,
} from '@scripsteam/scrips-signal-ds';
import {
  bulkExemptIds,
  collectLinkIds,
  fillSectionNegative,
  railGroups,
  sectionSymptomChipIds,
  setAnswer,
  withSymptomChipDefaults,
} from '../lib/questionnaire-groups';
import { isChiefComplaintHpiMarker } from '../lib/chief-complaint-hpi';
import { riskBridgeScope } from '../risk/lib/risk-qr-bridge';
// The static-text item type, as the app already names it (the #562 readout lines are drawn as one).
import { STATIC_LINE_TYPE } from '../lib/readout-lines';

export type AnswerMap = Record<string, QAnswerValue>;

/** linkId → stored value, for enableWhen and for "is this answered". */
export function answerMap(response: QuestionnaireResponse): AnswerMap {
  const out: AnswerMap = {};
  for (const a of response.answers) out[a.linkId] = a.value;
  return out;
}

/** An answer counts when it holds something; `false` and `0` count, '' and [] do not. */
export function isAnswered(value: QAnswerValue): boolean {
  return !(value === undefined || value === '' || (Array.isArray(value) && value.length === 0));
}

/** One answer to write. `value: undefined` removes the answer (not asked / cleared). */
export interface AnswerUpdate {
  linkId: string;
  value: QAnswerValue;
}

/** The response with `updates` applied, every other answer untouched (`setAnswer`, PROD-1234). */
export function applyUpdates(response: QuestionnaireResponse, updates: readonly AnswerUpdate[]): QuestionnaireResponse {
  return updates.reduce((r, u) => setAnswer(r, u.linkId, u.value), response);
}

/** Why a block's change was not written. The host turns each into one plain line. */
export type RefusalReason =
  /** A bulk change would have replaced a recorded answer. */
  | 'bulk-overwrite'
  /** A bulk change would have answered a risk item (suicide, self-harm, harm to others). */
  | 'bulk-risk'
  /** A bulk exam change would have set a +/- finding (AC-08.1: bulk sets normal statements only). */
  | 'bulk-finding'
  /** A bulk exam change would have marked a normal statement while its system holds an abnormal
   *  finding (AC-08.1, the DS's own eligibility rule since #434's host contract). */
  | 'bulk-abnormal-system'
  /** A bulk exam change would have set an item in a system nobody has answered in (desk ruling on
   *  Andrew's follow-up 2: "Normal on the rest" never reaches an untouched system). */
  | 'bulk-untouched-system';

export interface Refusal {
  linkId: string;
  label: string;
  reason: RefusalReason;
}

export interface GuardedChange {
  updates: AnswerUpdate[];
  refused: Refusal[];
}

/** Every answerable item under `items`, depth first, skipping anything enableWhen hides (a hidden
 *  item hides its whole subtree, FHIR). A question's own child questions (FHIR allows them) are
 *  listed after it, so none is ever silently dropped. */
export function enabledLeaves(items: readonly QItem[], values: AnswerMap): QItem[] {
  const out: QItem[] = [];
  const walk = (list: readonly QItem[]) => {
    for (const it of list) {
      if (!qrItemEnabled(it, values)) continue;
      if (it.type !== 'group') out.push(it);
      walk(it.item ?? []);
    }
  };
  walk(items);
  return out;
}

/**
 * A system group's items split into the block's findings and the REST, each item in exactly one: a
 * finding goes to the block and its own child questions (if any) to the rest; any other question
 * goes to the rest WHOLE, children included, so the renderer that draws it draws them too and no
 * item is ever drawn — or merged — by two writers.
 */
export function splitFindings(
  items: readonly QItem[],
  values: AnswerMap,
  isFinding: (item: QItem) => boolean,
): { findings: QItem[]; rest: QItem[] } {
  const findings: QItem[] = [];
  const rest: QItem[] = [];
  const walk = (list: readonly QItem[]) => {
    for (const it of list) {
      if (!qrItemEnabled(it, values)) continue;
      if (it.type === 'group') walk(it.item ?? []);
      else if (isFinding(it)) {
        findings.push(it);
        walk(it.item ?? []);
      } else rest.push(it);
    }
  };
  walk(items);
  return { findings, rest };
}

/** `item` with every item in `skip` removed at any depth — what a renderer run must never draw or
 *  merge because another writer owns it (the section narrative, Codex P1 2 Oct). Returns `item`
 *  itself when nothing under it is skipped. */
export function without(item: QItem, skip: ReadonlySet<string>): QItem {
  if (skip.size === 0 || !item.item) return item;
  const kids = item.item.filter((c) => !skip.has(c.linkId)).map((c) => without(c, skip));
  return kids.length === item.item.length && kids.every((k, i) => k === item.item?.[i]) ? item : { ...item, item: kids };
}

/** linkIds of every computed item under `items` (any depth, whatever its type or children). */
export function calculatedIds(items: readonly QItem[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: readonly QItem[]) => {
    for (const it of list) {
      if (it.calculatedExpression) ids.add(it.linkId);
      walk(it.item ?? []);
    }
  };
  walk(items);
  return ids;
}

/** The authored group cut down to `children`, for the DS renderer to draw beside a block. Keeps the
 *  group's own linkId (so the merge scope is the real one) and every other field of the group. */
export function prunedGroup(group: QItem, children: readonly QItem[]): QItem {
  return { ...group, item: [...children] };
}

/** Loose leaves (no authored group above them) wrapped so the renderer can draw them as one run.
 *  The wrapper's linkId cannot collide with an authored one (`::`), and it carries no answer. */
export function looseGroup(children: readonly QItem[]): QItem {
  return { linkId: `${children[0]?.linkId ?? 'loose'}::run`, type: 'group', item: [...children] };
}

/** A finding the clinician can toggle: not read-only, not computed. */
const writable = (item: QItem): boolean => !item.readOnly && !item.calculatedExpression;

/** A complaint-map key names a system by its code, or by the code's prefix ("ros-gi" names
 *  "ros-gi-upper", never "ros-gin"). PROD-2789. */
export function systemMatchesKey(code: string, key: string): boolean {
  return code === key || code.startsWith(`${key}-`);
}

/** Move the complaint map's systems (PROD-2789 `systemOrderFor`, linkId keys) to the front; the
 *  rest keep their authored order. Never hides one. */
export function orderSystems<T extends { code: string }>(systems: readonly T[], order: readonly string[] | undefined): T[] {
  if (!order || order.length === 0) return [...systems];
  const rank = (code: string) => {
    const i = order.findIndex((key) => systemMatchesKey(code, key));
    return i === -1 ? order.length : i;
  };
  return systems
    .map((s, i) => ({ s, i, r: rank(s.code) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.s);
}

// ── Repeated group headings (D-71 amendment) ─────────────────────────────────────────────────

const sameHeading = (a: string, b: string) => a.replace(/\s+/g, ' ').trim().toLowerCase() === b.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * D-71 amendment (design desk, 6 Oct): the frame never draws a template group heading that repeats, ignoring
 * case and whitespace, the section or block title directly above it — "Objective" as the first exam row under
 * the Objective section. A rail-level group whose title repeats one of `repeats` and that holds groups of its
 * own is drawn AS those groups (each with its own heading); its direct items stay with it, so nothing authored
 * is dropped. A disabled group is left whole for the walk to skip. The template itself is untouched.
 */
export function withoutRepeatedHeadings(groups: readonly QItem[], values: AnswerMap, repeats: readonly string[] = []): QItem[] {
  const said = repeats.filter((r) => r.trim().length > 0);
  if (said.length === 0) return [...groups];
  return groups.flatMap((g) => {
    if (g.type !== 'group' || !g.text || !said.some((r) => sameHeading(r, g.text as string)) || !qrItemEnabled(g, values)) return [g];
    const children = g.item ?? [];
    const subgroups = children.filter((c) => c.type === 'group');
    if (subgroups.length === 0) return [g];
    const direct = children.filter((c) => c.type !== 'group');
    return [...(direct.length > 0 ? [{ ...g, item: direct }] : []), ...subgroups];
  });
}

// ── Review of systems ────────────────────────────────────────────────────────────────────────

export interface RosModel {
  /** One per system group that holds at least one +/- finding. */
  systems: RosSystem[];
  /** Everything else in the section (normal statements, text, choices…) as pruned groups: drawn by
   *  the renderer beside the block, so nothing authored is dropped. */
  others: QItem[];
}

/**
 * The ROS block's systems. `section` is the chip-carrying section (after `withSymptomChipDefaults`),
 * so the DS's own predicates decide what a +/- finding is. A system = one group of the section's
 * rail level (`railGroups`: one group per system, or the children of a lone wrapper).
 */
export function rosModel(section: Questionnaire, values: AnswerMap, systemOrder?: readonly string[], repeats: readonly string[] = []): RosModel {
  const systems: RosSystem[] = [];
  const others: QItem[] = [];
  const loose: QItem[] = [];
  const exempt = bulkExemptIds(section.item);
  for (const group of withoutRepeatedHeadings(railGroups(section), values, repeats)) {
    if (!qrItemEnabled(group, values)) continue;
    if (group.type !== 'group') {
      loose.push(group);
      continue;
    }
    const { findings, rest } = splitFindings(group.item ?? [], values, (l) => qrIsPosNegChip(l) && writable(l));
    if (rest.length) others.push(prunedGroup(group, rest));
    if (findings.length === 0) continue;
    systems.push({
      code: group.linkId,
      display: group.text ?? group.linkId,
      items: findings.map((f) => ({ linkId: f.linkId, text: f.text ?? f.linkId, riskItem: exempt.has(f.linkId) || undefined })),
    });
  }
  if (loose.length) others.push(looseGroup(loose));
  return { systems: orderSystems(systems, systemOrder), others };
}

/** Stored booleans → the block's findings. Unset stays absent (not asked). */
export function rosFindings(systems: readonly RosSystem[], values: AnswerMap): RosFindings {
  const out: RosFindings = {};
  for (const s of systems) {
    for (const i of s.items) {
      const v = values[i.linkId];
      if (v === true) out[i.linkId] = 'reports';
      else if (v === false) out[i.linkId] = 'denies';
    }
  }
  return out;
}

const ROS_WIRE: Partial<Record<RosFinding, boolean>> = { reports: true, denies: false };

/**
 * The states the boolean wire can hold, handed to the blocks as `allowedStates` (signal-ds #434), so
 * the "⋯" never offers Doesn't know / Declined / Not examined / Unable to assess until G18 (PROD-2821)
 * stores them. Never offer what cannot be saved (judge, 2 Oct).
 */
export const ROS_STORABLE: RosFinding[] = ['reports', 'denies'];
export const EXAM_STORABLE: ExamFindingState[] = ['abnormal', 'normal'];

/**
 * The block's next findings → the answers to write, with every refusal named. `origin` is the
 * block's own report (`'user'` | `'bulk'`, signal-ds #434). A change touching more than one item is
 * treated as bulk whatever it says (defence: a bulk is held to the bulk rules even if mislabelled);
 * a system's Clear (`'user'`, every row to unset) is always allowed.
 */
export function rosChanges(
  systems: readonly RosSystem[],
  values: AnswerMap,
  prev: RosFindings,
  next: RosFindings,
  origin: ChangeOrigin = 'user',
): GuardedChange {
  const items = systems.flatMap((s) => s.items);
  const changed = items.filter((i) => prev[i.linkId] !== next[i.linkId]);
  const bulk = origin === 'bulk' || changed.length > 1;
  const updates: AnswerUpdate[] = [];
  const refused: Refusal[] = [];
  for (const item of changed) {
    const target = next[item.linkId];
    const label = item.text;
    if (target === undefined) {
      // A Clear (one item, or a system's Clear) is the clinician's own act: back to not asked. A BULK
      // never removes a recorded answer (Codex P1, 2 Oct): only the block's 'user' origin may clear.
      if (origin === 'bulk' && isAnswered(values[item.linkId])) {
        refused.push({ linkId: item.linkId, label, reason: 'bulk-overwrite' });
        continue;
      }
      updates.push({ linkId: item.linkId, value: undefined });
      continue;
    }
    // A state the wire cannot hold is never written. The block is handed `ROS_STORABLE`, so it no
    // longer offers one; this only guards a block that ignored it.
    const wire = ROS_WIRE[target];
    if (wire === undefined) continue;
    if (bulk && isAnswered(values[item.linkId])) {
      refused.push({ linkId: item.linkId, label, reason: 'bulk-overwrite' });
      continue;
    }
    if (bulk && item.riskItem) {
      refused.push({ linkId: item.linkId, label, reason: 'bulk-risk' });
      continue;
    }
    updates.push({ linkId: item.linkId, value: wire });
  }
  return { updates, refused };
}

/** Systems that already hold an answer: always shown, so a recorded finding is never hidden. */
export function rosAnsweredSystems(systems: readonly RosSystem[], values: AnswerMap): string[] {
  return systems.filter((s) => s.items.some((i) => isAnswered(values[i.linkId]))).map((s) => s.code);
}

// ── Physical exam ────────────────────────────────────────────────────────────────────────────

export type ExamKind = 'posneg' | 'normal';

/**
 * The exam as ONE ExamBlock (contract §1b, judge J3, 2 Oct): each template group under PE is ONE
 * ExamSystem, labelled with the group's own text (e.g. "HEENT"); each present/absent item in it is one
 * finding chip in that system's row (`ExamSystem.items`, signal-ds #444), and the block composes the
 * system's sentence from the answered items only. Items that are not present/absent (a pick-list such
 * as "Acute distress", free text…) stay as note lines inside the block, after the chips (`others`).
 */
export interface ExamModel {
  systems: ExamSystem[];
  /** Per item linkId: a +/- finding or a normal statement. */
  kinds: Record<string, ExamKind>;
  /** Per item linkId: bulk-exempt (inherited). */
  risk: Record<string, boolean>;
  /** Per item linkId: its system's code. */
  systemOf: Record<string, string>;
  /** Groups carrying the `vitals-block` marker, drawn with the host's `resolveVitals`. */
  vitals: QItem[];
  others: QItem[];
}

export function examModel(section: Questionnaire, values: AnswerMap, systemOrder?: readonly string[], repeats: readonly string[] = []): ExamModel {
  const systems: ExamSystem[] = [];
  const kinds: Record<string, ExamKind> = {};
  const risk: Record<string, boolean> = {};
  const systemOf: Record<string, string> = {};
  const vitals: QItem[] = [];
  const others: QItem[] = [];
  const loose: QItem[] = [];
  const exempt = bulkExemptIds(section.item);
  const visit = (group: QItem) => {
    if (!qrItemEnabled(group, values)) return;
    if (group.type !== 'group') {
      loose.push(group);
      return;
    }
    const { findings, rest } = splitFindings(group.item ?? [], values, (l) => (qrIsPosNegChip(l) || qrIsNormalChip(l)) && writable(l));
    if (rest.length) others.push(prunedGroup(group, rest));
    if (findings.length === 0) return;
    systems.push({
      code: group.linkId,
      display: group.text ?? group.linkId,
      items: findings.map((f) => {
        const kind: ExamKind = qrIsNormalChip(f) ? 'normal' : 'posneg';
        kinds[f.linkId] = kind;
        risk[f.linkId] = exempt.has(f.linkId);
        systemOf[f.linkId] = group.linkId;
        // A +/- finding is 'finding' (＋ present / − absent; "No <finding>" when absent, never set in
        // bulk); a normal statement is 'normal' (the two-state normal chip). The block words them.
        return { linkId: f.linkId, text: f.text ?? f.linkId, kind: kind === 'posneg' ? ('finding' as const) : ('normal' as const), riskItem: risk[f.linkId] || undefined };
      }),
    });
  };
  // The vitals marker can sit anywhere — beside a lone PE wrapper, inside it, or in a system group —
  // so it is collected at every depth (Codex P2, 2 Oct), and the system walk never visits it.
  for (const marker of vitalsMarkers(section.item)) if (qrItemEnabled(marker, values)) vitals.push(marker);
  // PROD-2789: a vitals marker BESIDE a lone wrapper (the v4 Studio publish: PE › "Vitals" + "Physical
  // Exam") must not stop the rail descending into the wrapper's systems — without this the whole exam was
  // ONE row ("Physical Exam") and no complaint system could open first. The markers are collected above,
  // so the system walk reads the section without them, at every depth.
  for (const group of withoutRepeatedHeadings(railGroups({ ...section, item: withoutVitalsMarkers(section.item) }), values, repeats)) visit(group);
  if (loose.length) others.push(looseGroup(loose));
  return { systems: orderSystems(systems, systemOrder), kinds, risk, systemOf, vitals, others };
}

/** Every item of the exam's systems, in row order. */
const examItems = (model: ExamModel) => model.systems.flatMap((s) => s.items ?? []);

/** Stored booleans → ExamBlock findings, keyed by item linkId. A normal statement only stores `true`. */
export function examFindings(model: ExamModel, values: AnswerMap): ExamFindings {
  const out: ExamFindings = {};
  for (const item of examItems(model)) {
    const v = values[item.linkId];
    if (model.kinds[item.linkId] === 'posneg') {
      if (v === true) out[item.linkId] = { state: 'abnormal' };
      else if (v === false) out[item.linkId] = { state: 'normal' };
    } else if (v === true) {
      out[item.linkId] = { state: 'normal' };
    }
  }
  return out;
}

function examWire(kind: ExamKind, f: ExamFinding): boolean | undefined {
  if (f.state === 'normal') return kind === 'posneg' ? false : true;
  if (f.state === 'abnormal') return kind === 'posneg' ? true : undefined;
  return undefined; // not examined · declined · unable to assess: not on the boolean wire yet
}

/**
 * The block's next findings → the answers to write. A bulk (the block's `'bulk'`, or a multi-item
 * change) mirrors the block's own eligibility, so the host never writes what the block did not count:
 * unanswered, not a risk item, a normal statement (never a +/- finding), never while ITS system holds
 * an abnormal finding; and a bulk across systems only in systems the clinician has answered in (a
 * row's own Normal may fill its untouched row).
 */
export function examChanges(
  model: ExamModel,
  values: AnswerMap,
  prev: ExamFindings,
  next: ExamFindings,
  origin: ChangeOrigin = 'user',
): GuardedChange {
  const items = examItems(model);
  const changed = items.filter((i) => prev[i.linkId]?.state !== next[i.linkId]?.state);
  const bulk = origin === 'bulk' || changed.length > 1;
  const inSystem = (code: string) => items.filter((i) => model.systemOf[i.linkId] === code);
  const systemAbnormal = (code: string) => inSystem(code).some((i) => model.kinds[i.linkId] === 'posneg' && values[i.linkId] === true);
  const systemTouched = (code: string) => inSystem(code).some((i) => isAnswered(values[i.linkId]));
  // A bulk inside ONE system is that system's own "Normal" (signal-ds #444 R1): the clinician acted on
  // that row, so it may fill an untouched system. Only a bulk across several systems ("Normal on the
  // rest") is held to "never an untouched system" — otherwise a row's Normal would say it recorded
  // statements the host then silently refused.
  const bulkSystems = new Set(changed.map((i) => model.systemOf[i.linkId]));
  const acrossSystems = bulkSystems.size > 1;
  const updates: AnswerUpdate[] = [];
  const refused: Refusal[] = [];
  for (const item of changed) {
    const target = next[item.linkId];
    const label = item.text;
    if (!target) {
      // As in rosChanges: only a 'user' change may clear; a bulk never removes a recorded answer.
      if (origin === 'bulk' && isAnswered(values[item.linkId])) {
        refused.push({ linkId: item.linkId, label, reason: 'bulk-overwrite' });
        continue;
      }
      updates.push({ linkId: item.linkId, value: undefined });
      continue;
    }
    const kind = model.kinds[item.linkId];
    // Never written when the wire cannot hold it (see rosChanges); the block is handed EXAM_STORABLE.
    const wire = examWire(kind, target);
    if (wire === undefined) continue;
    const system = model.systemOf[item.linkId];
    if (bulk && isAnswered(values[item.linkId])) {
      refused.push({ linkId: item.linkId, label, reason: 'bulk-overwrite' });
      continue;
    }
    if (bulk && model.risk[item.linkId]) {
      refused.push({ linkId: item.linkId, label, reason: 'bulk-risk' });
      continue;
    }
    if (bulk && kind === 'posneg') {
      refused.push({ linkId: item.linkId, label, reason: 'bulk-finding' });
      continue;
    }
    if (bulk && systemAbnormal(system)) {
      refused.push({ linkId: item.linkId, label, reason: 'bulk-abnormal-system' });
      continue;
    }
    if (bulk && acrossSystems && !systemTouched(system)) {
      refused.push({ linkId: item.linkId, label, reason: 'bulk-untouched-system' });
      continue;
    }
    updates.push({ linkId: item.linkId, value: wire });
  }
  return { updates, refused };
}

// ── Risk: All Neg under G7 ───────────────────────────────────────────────────────────────────

/**
 * The Risk step's All Neg (contract G7) through #559's `fillSectionNegative`, the ONE implementation
 * the OFF path uses too: it fills only unanswered findings (+/- → false, normal statement → true) and
 * never a bulk-exempt risk item (inherited down the tree). This returns what it wrote as updates (for
 * the shared write path and Undo), and names each unanswered finding it left — the risk items — so the
 * clinician knows they are still theirs to answer. No +/- finding in scope → nothing (OFF's rule).
 */
export function riskAllNeg(full: QuestionnaireResponse, section: Questionnaire): GuardedChange {
  const before = answerMap(full);
  const after = fillSectionNegative(full, section);
  const updates: AnswerUpdate[] = after.answers
    .filter((a) => before[a.linkId] === undefined && isAnswered(a.value))
    .map((a) => ({ linkId: a.linkId, value: a.value }));
  const refused: Refusal[] = [];
  if (sectionSymptomChipIds(section, full).length > 0) {
    const filled = new Set(updates.map((u) => u.linkId));
    const walk = (list: readonly QItem[]) => {
      for (const it of list) {
        if (!qrItemEnabled(it, before)) continue;
        walk(it.item ?? []);
        if (it.type === 'group' || !writable(it) || !(qrIsPosNegChip(it) || qrIsNormalChip(it))) continue;
        if (!isAnswered(before[it.linkId]) && !filled.has(it.linkId)) {
          refused.push({ linkId: it.linkId, label: it.text ?? it.linkId, reason: 'bulk-risk' });
        }
      }
    };
    walk(section.item);
  }
  return { updates, refused };
}

/**
 * What All Neg's result line names as skipped (desk, 2 Oct): a risk GROUP by its own text when every
 * finding in it was skipped ("Suicide risk assessment is not set in bulk: ask directly"); otherwise
 * each skipped ITEM by its text ("Aggressive …"), so a group whose other findings WERE set is never
 * named as skipped. De-duplicated, in order.
 */
export function skippedNames(items: readonly QItem[], refused: readonly Refusal[]): string[] {
  const skipped = new Set(refused.map((r) => r.linkId));
  const owner = new Map<string, QItem | undefined>();
  const walk = (list: readonly QItem[], group: QItem | undefined) => {
    for (const it of list) {
      if (it.type === 'group') walk(it.item ?? [], it);
      else owner.set(it.linkId, group);
    }
  };
  walk(items, undefined);
  const isFinding = (it: QItem) => it.type !== 'group' && writable(it) && (qrIsPosNegChip(it) || qrIsNormalChip(it));
  // Every finding under the group, nested groups included (Codex P2): a filled finding anywhere in it
  // means the group was not skipped as a whole.
  const findingsUnder = (list: readonly QItem[]): QItem[] =>
    list.flatMap((it) => (it.type === 'group' ? findingsUnder(it.item ?? []) : isFinding(it) ? [it] : []));
  const names = refused.map((r) => {
    const group = owner.get(r.linkId);
    const findings = findingsUnder(group?.item ?? []);
    const wholeGroup = !!group?.text && findings.length > 0 && findings.every((f) => skipped.has(f.linkId));
    return wholeGroup ? (group?.text as string) : r.label;
  });
  return [...new Set(names)];
}

/** "A", "A and B", "A, B and C". */
export const joinNames = (names: readonly string[]): string =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/** A section's Clear: every answer under the section's linkIds (its narrative included) removed — the
 *  same set OFF's `mergeSectionResponse(full, section, { answers: [] })` drops. */
export function clearSection(items: readonly QItem[], values: AnswerMap): AnswerUpdate[] {
  return [...collectLinkIds([...items])].filter((id) => values[id] !== undefined).map((linkId) => ({ linkId, value: undefined }));
}

/** Undo of a bulk fill: each answer it set goes back to unanswered, but ONLY if it still holds the
 *  value the fill wrote. An answer the clinician changed since is theirs and is left alone. */
export function undoFill(filled: readonly AnswerUpdate[], values: AnswerMap): AnswerUpdate[] {
  return filled.filter((u) => values[u.linkId] === u.value).map((u) => ({ linkId: u.linkId, value: undefined }));
}

/** Undo of a Clear (audit FV2-23, 6 Oct: "All Neg offers Undo; Clear does not"). `cleared` holds the
 *  answers as they were BEFORE the clear. Each goes back only where its item is still unanswered: an
 *  answer the clinician entered after the clear is theirs and is left alone. */
export function undoClear(cleared: readonly AnswerUpdate[], values: AnswerMap): AnswerUpdate[] {
  return cleared.filter((u) => values[u.linkId] === undefined && u.value !== undefined);
}

// ── Narrative (HPI and every non-finding item) ──────────────────────────────────────────────

export type NarrativeRow =
  /** A display item (and a readout line, which the page already turned into one, #562). */
  | { kind: 'text'; item: QItem }
  /** A coded question: PickListChip writing the option's key to the same linkId. `detail`: a text item the template
   *  authored for the SAME fact under the same label, drawn beside the pick as its K6 detail (G7, PROD-2947). */
  | { kind: 'pick'; item: QItem; options: PickListOption[]; multiple: boolean; detail?: QItem }
  /** A yes/no question: PickListChip over the boolean. Unset reads "Not asked", never "No". */
  | { kind: 'yes-no'; item: QItem }
  /** A required number with no other home: BlankChip, listed at Sign until filled. */
  | { kind: 'blank'; item: QItem }
  /** An optional number (a count, a measure): a compact DS number field with its unit, on a note line
   *  (desk, 2 Oct: never a full-width empty box). A REQUIRED number stays a BlankChip. */
  | { kind: 'number'; item: QItem }
  /** A free-text item (`string` / `text`): the DS NoteFieldLine, a line of the note (signal-ds #434),
   *  never the old renderer's card inside a block (judge B3). */
  | { kind: 'line'; item: QItem }
  /** A run of authored finding chips (`symptom-chip` booleans: +/- or a normal statement): DS
   *  SymptomChips on one line, writing true / false / unset to their own answers. */
  | { kind: 'chips'; items: QItem[] }
  /** A run of items no note line draws yet (dates, attachments, optional numbers…): the DS renderer,
   *  scoped to exactly these items (the authored group, pruned to the run), merging into the same
   *  response. Structured Note v2 has no date or attachment line; these wait for a design ruling. */
  | { kind: 'fields'; group: QItem };

export interface NarrativeGroup {
  linkId: string;
  title?: string;
  rows: NarrativeRow[];
  /**
   * Frame v2: the group's title is not drawn as an eyebrow (`stepNarrative`), because the note already
   * says it: the group is the step's PAGE WRAPPER (its linkId is the step key, or it is the step's only
   * top-level group — the block names the step), or the same group is drawn as a system ROW whose label
   * is its title (ROS / PE). The title stays on the group for the row and for accessible names.
   */
  untitled?: boolean;
}

export const YES_NO_OPTIONS: PickListOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const optionLabel = (o: QAnswerOption): string => o.valueCoding?.display ?? o.valueCoding?.code ?? o.valueString ?? '';

export function pickOptions(item: QItem): PickListOption[] {
  return (item.answerOption ?? []).map((o) => ({ value: qrOptionValue(o), label: optionLabel(o) }));
}

function rowFor(item: QItem): NarrativeRow | 'field' | 'chip' {
  // A question carrying child questions goes whole to the renderer, which draws the children too.
  if ((item.item?.length ?? 0) > 0) return 'field';
  if (item.type === STATIC_LINE_TYPE) return { kind: 'text', item };
  if (item.type === 'boolean' && (qrIsPosNegChip(item) || qrIsNormalChip(item)) && writable(item)) return 'chip';
  if (item.readOnly || item.calculatedExpression || item.itemControl) return 'field';
  if ((item.type === 'choice' || item.type === 'open-choice') && (item.answerOption?.length ?? 0) > 0) {
    return { kind: 'pick', item, options: pickOptions(item), multiple: !!item.repeats };
  }
  if (item.type === 'boolean') return { kind: 'yes-no', item };
  if ((item.type === 'integer' || item.type === 'decimal') && item.required) return { kind: 'blank', item };
  if (item.type === 'integer' || item.type === 'decimal') return { kind: 'number', item };
  if (item.type === 'string' || item.type === 'text') return { kind: 'line', item };
  return 'field';
}

/**
 * Does this group carry a computed score (a `calculatedExpression` anywhere under it)?
 *
 * DEVIATION from §1b (logged in the PR): such a group is NOT drawn as an InstrumentCard. The card
 * has no score by design ("Scored instruments are a different contract; do not add a score prop
 * here") and its completed state prints "The screen has no total and no score" — false for a PHQ-9
 * or GAD-7. So a scored group stays on the DS renderer, which computes and SHOWS the total, and
 * the host drops the computed value from what it saves (UC-29f: a score is never an answer).
 */
export function isInstrumentGroup(group: QItem): boolean {
  return group.type === 'group' && calculatedIds(group.item ?? []).size > 0;
}

/** The two host-resolved markers: drawn by their own host component, never as narrative. */
export function isMarkerGroup(group: QItem): boolean {
  return group.type === 'group' && (isChiefComplaintHpiMarker(group) || group.itemControl === 'vitals-block');
}

/** Every vitals-block marker group under `items`, at any depth, in authored order. */
/** The items with every vitals marker removed, at every depth (the exam's system walk never visits one). */
export function withoutVitalsMarkers(items: readonly QItem[]): QItem[] {
  return items
    .filter((it) => !(it.type === 'group' && it.itemControl === 'vitals-block'))
    .map((it) => (it.type === 'group' && it.item ? { ...it, item: withoutVitalsMarkers(it.item) } : it));
}

export function vitalsMarkers(items: readonly QItem[]): QItem[] {
  const out: QItem[] = [];
  const walk = (list: readonly QItem[]) => {
    for (const it of list) {
      if (it.type !== 'group') continue;
      if (it.itemControl === 'vitals-block') out.push(it);
      else walk(it.item ?? []);
    }
  };
  walk(items);
  return out;
}

/**
 * The narrative rows for `items` (a section's groups, or the leftovers of a finding block), in
 * authored order. A nested group becomes its own titled run. A group with nothing enabled draws
 * nothing (§1b). `skip` holds linkIds drawn elsewhere (the section's free-text narrative).
 */
export function narrativeGroups(
  items: readonly QItem[],
  values: AnswerMap,
  skip: ReadonlySet<string>,
  /** Groups drawn WHOLE by the renderer whatever their items are (Risk's Fall / Pain scales, which
   *  the page bridges to EncounterMetaData as one unit). */
  whole: ReadonlySet<string> = new Set(),
): NarrativeGroup[] {
  const out: NarrativeGroup[] = [];
  const emit = (owner: QItem | undefined, linkId: string, title: string | undefined, list: readonly QItem[]) => {
    const rows: NarrativeRow[] = [];
    const nested: QItem[] = [];
    let run: QItem[] = [];
    let chips: QItem[] = [];
    const flush = () => {
      if (run.length) rows.push({ kind: 'fields', group: without(owner ? prunedGroup(owner, run) : looseGroup(run), skip) });
      run = [];
      if (chips.length) rows.push({ kind: 'chips', items: chips });
      chips = [];
    };
    for (const it of list) {
      if (skip.has(it.linkId) || !qrItemEnabled(it, values)) continue;
      if (it.type === 'group') {
        nested.push(it);
        continue;
      }
      const row = rowFor(it);
      if (row === 'field') {
        if (chips.length) flush();
        run.push(it);
      } else if (row === 'chip') {
        if (run.length) flush();
        chips.push(it);
      } else {
        flush();
        rows.push(row);
      }
    }
    flush();
    if (rows.length) out.push({ linkId, title, rows });
    for (const g of nested) group(g);
  };
  /** A group: a marker is drawn elsewhere; a scored group goes whole to the renderer; any other
   *  becomes its own titled run. */
  const group = (g: QItem) => {
    if (isMarkerGroup(g)) return;
    if (isInstrumentGroup(g) || whole.has(g.linkId)) out.push({ linkId: g.linkId, title: g.text, rows: [{ kind: 'fields', group: without(g, skip) }] });
    else emit(g, g.linkId, g.text, g.item ?? []);
  };
  const loose: QItem[] = [];
  for (const it of items) {
    if (it.type === 'group') {
      if (loose.length) emit(undefined, `${loose[0].linkId}::run`, undefined, loose.splice(0));
      if (qrItemEnabled(it, values)) group(it);
    } else {
      loose.push(it);
    }
  }
  if (loose.length) emit(undefined, `${loose[0].linkId}::run`, undefined, loose);
  return out;
}

/**
 * A structured step's narrative rows, split by WHERE they draw (judge B3, 2 Oct):
 *  · `inBlock` — the lines of the note (chips, blanks, NoteFieldLine, static lines), inside the
 *    step's NoteBlock;
 *  · `outside` — the renderer runs (dates, attachments, optional numbers, scored instruments): no
 *    Structured Note v2 line exists for them yet, so they draw AFTER the block, never as the old
 *    renderer card inside it, until the design desk rules.
 * HPI reads its whole section; ROS and PE read what their finding blocks leave (`others`).
 */
export function stepNarrative(
  step: 'hpi' | 'risk' | 'ros' | 'pe',
  section: Questionnaire,
  values: AnswerMap,
  skip: ReadonlySet<string>,
  /** The section and block titles drawn above the step (D-71 amendment): the finding blocks draw a group
   *  that repeats one as its own groups, and the narrative must split the same way. */
  repeats: readonly string[] = [],
): { inBlock: NarrativeGroup[]; outside: QItem[]; riskSystems: Record<string, RosSystem> } {
  const model =
    step === 'ros'
      ? rosModel(withSymptomChipDefaults(section, 'ros'), values, undefined, repeats)
      : step === 'pe'
        ? examModel(withSymptomChipDefaults(section, 'pe'), values, undefined, repeats)
        : undefined;
  const items = model ? model.others : section.item;
  // Titles the note already says (the independent review, 6 Oct — "Subjective" under Subjective, "Review
  // of Systems" under ROS): the step's page wrapper, and a group whose findings draw as a system row.
  const tops = section.item.filter((g) => g.type === 'group');
  const pageWrappers = new Set(tops.filter((g) => g.linkId === step || tops.length === 1).map((g) => g.linkId));
  const rowGroups = new Set((model?.systems ?? []).map((sys) => sys.code));
  // Risk's Fall / Pain scales go whole to the renderer: the page bridges them as one unit, holds them
  // back until their saved assessment has loaded, and offers their Done (the Risk canvas does all three).
  const whole = new Set<string>();
  if (step === 'risk') {
    const scope = riskBridgeScope(section.item);
    for (const g of [scope.fall, scope.pain]) if (g) whole.add(g.linkId);
  }
  const groups = narrativeGroups(items, values, skip, whole);
  const inBlock = groups
    .map((g) => ({
      ...g,
      rows: g.rows.filter((r) => r.kind !== 'fields'),
      ...(pageWrappers.has(g.linkId) || rowGroups.has(g.linkId) ? { untitled: true } : {}),
    }))
    .filter((g) => g.rows.length > 0);
  const outside = groups.flatMap((g) => g.rows.flatMap((r) => (r.kind === 'fields' ? [r.group] : [])));
  // Risk (desk, 2 Oct): a group whose items are ALL +/- findings is a system row — label | composed
  // "Reports … / Denies …" sentence | chips — drawn by ReviewOfSystemsBlock (history wording, the same
  // row pattern as ROS and PE). A mixed group keeps its heading and note lines.
  const riskSystems: Record<string, RosSystem> = {};
  if (step === 'risk') {
    const exempt = bulkExemptIds(section.item);
    for (const g of inBlock) {
      const only = g.rows.length === 1 && g.rows[0].kind === 'chips' ? g.rows[0].items : undefined;
      if (!g.title || !only || !only.every((it) => qrIsPosNegChip(it))) continue;
      riskSystems[g.linkId] = {
        code: g.linkId,
        display: g.title,
        items: only.map((it) => ({ linkId: it.linkId, text: it.text ?? it.linkId, riskItem: exempt.has(it.linkId) || undefined })),
      };
    }
  }
  return { inBlock, outside, riskSystems };
}

/** Every linkId a block draws itself (its note lines and chips) plus `skip` (the section narrative).
 *  Sorted, so a caller can key a memo on it: it changes only when what the block draws changes. */
export function drawnIds(inBlock: readonly NarrativeGroup[], skip: ReadonlySet<string>): string[] {
  const drawn = new Set<string>(skip);
  for (const g of inBlock) {
    for (const r of g.rows) {
      if (r.kind === 'chips') r.items.forEach((it) => drawn.add(it.linkId));
      else if (r.kind !== 'fields') drawn.add(r.item.linkId);
    }
  }
  return [...drawn].sort();
}

/**
 * Every item hidden right now by its enableWhen (and everything under a hidden group), sorted. A hidden item
 * draws nowhere, so the Risk canvas must not draw its group for it: the live F8 defect (6 Oct, dev) was the
 * CDC test's gestational-diabetes question, hidden for a man, left in the canvas, which drew an empty second
 * "Type 2 Risk Assessment · Clear" card under the block that already titles the group (L9: no empty titled card).
 */
export function hiddenIds(items: readonly QItem[], values: AnswerMap): string[] {
  const out = new Set<string>();
  const all = (it: QItem) => {
    out.add(it.linkId);
    (it.item ?? []).forEach(all);
  };
  const walk = (list: readonly QItem[]) => {
    for (const it of list) {
      if (!qrItemEnabled(it, values)) all(it);
      else walk(it.item ?? []);
    }
  };
  walk(items);
  return [...out].sort();
}

/**
 * The questionnaire the Risk canvas draws BELOW the Risk block: the whole template minus every item
 * the block draws as a note line or chip (and the section narrative), with any group left empty
 * dropped. So each answer has one writer, and the canvas keeps what only it does: Fall / Pain, their
 * load gate and Done, and anything with no note line yet.
 */
export function withoutDrawn(questionnaire: Questionnaire, drawn: ReadonlySet<string>): Questionnaire {
  const prune = (list: readonly QItem[]): QItem[] =>
    list
      .filter((it) => !drawn.has(it.linkId))
      .map((it) => (it.item ? { ...it, item: prune(it.item) } : it))
      .filter((it) => it.type !== 'group' || (it.item?.length ?? 0) > 0);
  return { ...questionnaire, item: prune(questionnaire.item) };
}

/** A chip's DS variant: a normal statement is the two-state "normal" tag; a +/- finding is posneg. */
export const chipVariant = (item: QItem): 'normal' | 'posneg' => (qrIsNormalChip(item) ? 'normal' : 'posneg');

/** A chip's stored boolean → the SymptomChip's state; unset stays unset (not asked). */
export function chipState(item: QItem, value: QAnswerValue): SymptomState {
  if (qrIsNormalChip(item)) return value === true ? 'present' : null;
  return value === true ? 'present' : value === false ? 'absent' : null;
}

/** The SymptomChip's next state → the stored boolean (a normal statement only ever stores true). */
export function chipValue(item: QItem, next: SymptomState): QAnswerValue {
  if (next === 'present') return true;
  if (next === 'absent' && !qrIsNormalChip(item)) return false;
  return undefined;
}

/** A pick's stored value → the chip's value. */
export function pickValue(row: Extract<NarrativeRow, { kind: 'pick' }>, value: QAnswerValue): string | string[] | null {
  if (row.multiple) return Array.isArray(value) ? value.map(String) : isAnswered(value) ? [String(value)] : [];
  if (Array.isArray(value)) return value.length ? String(value[0]) : null;
  return isAnswered(value) ? String(value) : null;
}

export function yesNoValue(value: QAnswerValue): string | null {
  return value === true ? 'true' : value === false ? 'false' : null;
}

// ── Required items still blank (the Sign list, UC-29c) ──────────────────────────────────────

export interface OpenRequired {
  linkId: string;
  label: string;
}

/** Required, enabled, answerable items with no answer. Read-only, computed and display items are
 *  never "required to sign": nobody can answer them. */
export function openRequired(section: Questionnaire, values: AnswerMap): OpenRequired[] {
  return enabledLeaves(section.item, values)
    .filter((it) => it.required && it.type !== STATIC_LINE_TYPE && !it.readOnly && !it.calculatedExpression && !isAnswered(values[it.linkId]))
    .map((it) => ({ linkId: it.linkId, label: it.text ?? it.linkId }));
}

/** The DOM id a Sign-list link scrolls to. One rule, used by the row and the link. */
export const noteItemAnchor = (linkId: string): string => `note-item-${linkId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
